import { apiSlice } from "./apiSlice";
import { MultilingualField } from "../../types/tender";
import { CodeBranch } from "../../types/code";
import { FetchArgs } from "@reduxjs/toolkit/query";
import { Language } from "../../consts/languages";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PURGE } from "redux-persist";

/*
  REDUX STORE stuff
*/

export enum CompanySuggestionsSource {
  KBO = "KBO",
  COMPANY_PAGE_ANY = "COMPANY_PAGE_ANY",
  COMPANY_PAGE_BUYERS = "COMPANY_PAGE_BUYERS",
  COMPANY_PAGE_PARTICIPANTS = "COMPANY_PAGE_PARTICIPANTS",
}

export interface MarketIntelligenceSearchState {
  tab?: MarketIntelligenceTab;
  companySearch?: MarketIntelligenceCompanySearch;
}

export enum MarketIntelligenceTab {
  OFFERS_AND_AWARDS,
  FRAMEWORKS,
  SEARCH_COMPANIES,
  SEARCH_CONTRACTING_AUTHORITIES,
}

export interface MarketIntelligenceCompanySearch {
  query: string;
  language: Language;
  vatNumbers: string[];
  subjectCodes: CodeBranch[];
  regionCodes: CodeBranch[];
  accreditations: CodeBranch[];
  source: CompanySuggestionsSource;
  page: number;
  pageSize: number;
}

const initialState: MarketIntelligenceSearchState = {};

export const marketIntelligenceSearchSlice = createSlice({
  initialState,
  name: "marketIntelligenceSearch",
  reducers: {
    updateMarketIntelligenceSearch(state, action: PayloadAction<MarketIntelligenceSearchState>) {
       state.tab = action.payload.tab;
       state.companySearch = action.payload.companySearch;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, (state) => {
        state.tab = undefined;
        state.companySearch = undefined;
    });
  },
});

export const { updateMarketIntelligenceSearch } = marketIntelligenceSearchSlice.actions;

/*
  API connection stuff
*/

export interface GetCompaniesByNameParams {
  name: string;
  source: CompanySuggestionsSource;
  page?: number;
  pageSize?: number;
}

export interface GetCompaniesByVatNumberInParams {
  vatNumbers: string[];
  source: CompanySuggestionsSource;
  page?: number;
  pageSize?: number;
}

export interface CompanySuggestionsDto {
  suggestions: CompanySuggestion[];
  totalHits: number;
}

export interface CompanySuggestion {
  uuid: string;
  vatNumber: string;
  name: MultilingualField;
  address?: MultilingualField;
  startDate?: string;
}

// export interface CompanyActivityDto {
//   description: string;
//   type: string;
// }

// export interface CompanyDto {
//   name: string;
//   uuid: string;
//   vatNumber: string;
//   street: string;
//   postalCode: string;
//   city: string;
//   country: string;
//   website: string;
//   phone: string;
//   email: string;
//   activities: CompanyActivityDto[];
//   startDate?: string;
//   juridicalForm?: string;
// }

export interface Denomination {
  languageIso: string;
  type: string;
  denomination: string;
}

export interface CompanyActivity {
  code: string;
  type: string;
  dutchDescription: string;
  frenchDescription: string;
  englishDescription: string;
}

export interface CompanyInformation {
  uuid: string;
  parentUuid: string;
  vatNumber: string; //COUNTRY ISO + NATIONAL IDENTIFIER
  denominations: Denomination[];
  street: string;
  houseNumber: string;
  box: string;
  postalCode: string;
  city: string;
  website: string;
  phone: string;
  email: string;
  activities: CompanyActivity[];
  juridicalForm: string;
  startDate: string;
  createdAt: string;
  updatedAt: string;
  accreditationsUpdatedAt: string;
  isActive: boolean;
}

// export interface CompanyRelationDto {
//   company: CompanyPage;
//   relation: CompanyDto;
//   sharedProjects: number;
//   directRelation: boolean;
// }

export interface CompanyCodeDto {
  cpvRoot: CodeBranch;
  count: number;
}

export interface RecentTenderDto {
  title: MultilingualField;
  type: string;
  uuid: string;
  publicationDate: string;
  totalHits: number;
  awardedValue?: number;
  awardCriteriumPricePercentage?: number;
}

export interface YearlyMetric {
  type: string;
  value: string;
  year: number;
}

export interface CompanyPageBid {
  tenderUuid: string;
  lotId: string;
  bidRole: string;
  bidValue: number;
  bidCurrency: string;
  publicationDate: string;
  deadline: string;
  awardDate: string;
  awardCriteriumPricePercentage: number;
  subjectCodes: string[];
  regionCodes: string[];
  accreditations: string[];
}

export interface CompanyPage extends CompanyInformation {
  establishments: CompanyInformation[];
  tendersContracted: string[]; //TENDERS WHERE COMPANY IS CONTRACTING AUTHORIT
  tendersParticipatedIn: string[]; //TENDERS WHERE COMPANY IS BIDDER BUT DID NOT WIN THE CONTRACT
  tendersWon: string[]; //TENDERS WHERE COMPANY GOT THE CONTRACT
  subjectCodes: string[];
  regionCodes: string[];
  accreditations: string[];
  description: string;
  descriptionSource: string;
  yearlyMetrics: YearlyMetric[];
  bids: CompanyPageBid[];
  averageBidValue?: number;
  percentageOfBidsWon?: number; // TODO remove percentageOfTendersWon
  percentageOfTendersWon?: number;
  percentageOfLotsThatOnlyDependOnPrice?: number;
  mostRecentTurnover?: number;
  mostRecentFTEs?: number;
}

export interface CompetitorDto {
  company: CompanyPage;
  sharedProjects: string[];
  countries: string[];
  tendersParticipatedInDuringTheLastFiveYears: number;
}

export interface CompetitorsResponse {
  competitors: CompetitorDto[];
  totalHits: number;
}

export interface BuyerDto {
  company: CompanyPage;
  sharedProjects: string[];
  sharedProjectsWon: string[];
  projectsContracted: number;
}

export interface BuyersResponse {
  buyers: BuyerDto[];
  totalHits: number;
}

export interface SupplierDto {
  company: CompanyPage;
  sharedProjects: string[];
}

export interface SuppliersResponse {
  suppliers: SupplierDto[];
  totalHits: number;
}

export interface ScreeningResponse {
  companies: CompanyWithSharedProjects[];
}

export interface CompanyWithSharedProjects {
  company: CompanyPage;
  sharedProjects: number;
}

export interface GetCompanyPageParams {
  uuid: string;
  page?: number;
  pageSize?: number;
}

export interface FindCompanyParams {
  kboNumber: string;
}

export interface TenderUuids {
  tenderUuids: string[];
}

export interface CompanyPageUuids {
  companyPageUuids: string[];
}

export interface ScreeningParams {
  tenderUuid: string;
  languageIso: string;
}

export interface CompanyPageSearchInput {
  query: string;
  language: Language;
  vatNumbers: string[];
  subjectCodes: string[];
  regionCodes: string[];
  accreditations: string[];
  source: CompanySuggestionsSource;
  page: number;
  pageSize: number;
}

export interface CompanyPageSearchOutput {
  pages: CompanyPage[];
  totalHits: number;
}

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getCompaniesByName: build.query<CompanySuggestionsDto, GetCompaniesByNameParams>({
      query: (params: GetCompaniesByNameParams) => {
        let page: number = params.page ? params.page : 0;
        let pageSize: number = params.pageSize ? params.pageSize : 20;
        return {
          url:
            `/shaggy/companies/suggest_names?prefix=` +
            params.name +
            `&source=` +
            params.source +
            `&page=` +
            page +
            `&pageSize=` +
            pageSize,
        };
      },
    }),
    getCompaniesByVatNumberIn: build.query<CompanySuggestionsDto, GetCompaniesByVatNumberInParams>({
      query: (params: GetCompaniesByVatNumberInParams) => {
        return {
          url:
            `/shaggy/companies/find_by_vat_numbers_in?source=` +
            params.source +
            `&vat_numbers=` +
            params.vatNumbers.join(","),
        };
      },
    }),
    companySearch: build.mutation<CompanyPageSearchOutput, MarketIntelligenceCompanySearch>({
      query: (body: MarketIntelligenceCompanySearch) => {
        let searchBody: CompanyPageSearchInput = {
          query: body.query,
          language: body.language,
          vatNumbers: body.vatNumbers,
          subjectCodes: body.subjectCodes.map((c) => c.code),
          regionCodes: body.regionCodes.map((c) => c.code),
          accreditations: body.accreditations.map((c) => c.code),
          source: body.source,
          page: body.page,
          pageSize: body.pageSize,
        };
        let args: FetchArgs = {
          url: `/shaggy/companies/search`,
          method: "POST",
          body: searchBody,
          // responseHandler: "json",
        };
        return args;
      },
    }),
    getCompanyPage: build.query<CompanyPage, GetCompanyPageParams>({
      query: (params: GetCompanyPageParams) => {
        return {
          url: `/shaggy/companies/company_page?uuid=` + params.uuid,
        };
      },
    }),
    getCompanyPageCompetitors: build.query<CompetitorsResponse, GetCompanyPageParams>({
      query: (params: GetCompanyPageParams) => {
        let page = params.page ? params.page : 0;
        let pageSize = params.pageSize ? params.pageSize : 20;
        return {
          url:
            `/shaggy/companies/company_page_competitors?uuid=` +
            params.uuid +
            `&page=` +
            page +
            `&page_size=` +
            pageSize,
        };
      },
    }),
    getCompanyPageBuyers: build.query<BuyersResponse, GetCompanyPageParams>({
      query: (params: GetCompanyPageParams) => {
        let page = params.page ? params.page : 0;
        let pageSize = params.pageSize ? params.pageSize : 20;
        return {
          url: `/shaggy/companies/company_page_buyers?uuid=` + params.uuid + `&page=` + page + `&page_size=` + pageSize,
        };
      },
    }),
    getCompanyPageSuppliers: build.query<SuppliersResponse, GetCompanyPageParams>({
      query: (params: GetCompanyPageParams) => {
        let page = params.page ? params.page : 0;
        let pageSize = params.pageSize ? params.pageSize : 20;
        return {
          url:
            `/shaggy/companies/company_page_suppliers?uuid=` + params.uuid + `&page=` + page + `&page_size=` + pageSize,
        };
      },
    }),
    getCompanyUuid: build.query<string, string>({
      query: (params: string) => {
        return {
          url: `/shaggy/companies/get_uuid_by_vat?vat=` + params,
          responseHandler: "text",
        };
      },
    }),
    getPotentialCompetitorsOrPartners: build.query<ScreeningResponse, ScreeningParams>({
      query: (params: ScreeningParams) => {
        return {
          url: `/shaggy/companies/screening?tenderUuid=` + params.tenderUuid + `&languageIso=` + params.languageIso,
        };
      },
    }),
  }),
});

export const {
  useCompanySearchMutation,
  useGetCompaniesByNameQuery,
  useGetCompanyPageQuery,
  useGetCompanyPageCompetitorsQuery,
  useGetCompanyPageSuppliersQuery,
  useGetCompanyPageBuyersQuery,
  useGetCompanyUuidQuery,
  useGetCompaniesByVatNumberInQuery,
  useGetPotentialCompetitorsOrPartnersQuery,
} = extendedApiSlice;
